
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ChatFeedbackInterface, FeedbackRepositoryInterface } from '@/services/QC-V2/feedback/interfaces';
import ChatMessages from '@/components/qa/chat/ChatMessages.vue';
import ChatDetails from '@/components/qa/chat/ChatDetails.vue';
import ChatPrivateNotes from '@/components/qa/chat/ChatPrivateNotes.vue';
import { ChatInterface, FilterRequestData, ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import FeedbacksFilters from '@/components/qa/feedback/FeedbacksFilters.vue';
import FeedbackDetails from '@/components/qa/feedback/FeedbackDetails.vue';
import ChatFeedbackReview from '@/components/qa/feedback/ChatFeedbackReview.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import ChatFeedbackComments from '@/components/qa/feedback/ChatFeedbackComments.vue';
import { Roles } from '@/services/Roles';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'feedbackRepositoryV2',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
    'profileRepository',
  ],
  components: {
    FeedbacksFilters,
    FeedbackDetails,
    ChatFeedbackReview,
    ChatFeedbackComments,
    ChatMessages,
    ChatDetails,
    ChatPrivateNotes,
  },
})
export default class AllFeedbacks extends Vue {
  private feedbackRepositoryV2!: FeedbackRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private feedback: ChatFeedbackInterface = {} as ChatFeedbackInterface;
  private chat: ChatInterface = {} as ChatInterface;

  private profilesMap: ProfileMapInterface = {};

  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];

  private scoreTypesMap: { [key: number]: string } = {};
  private scoreGradesMap: { [key: number]: string } = {};

  private loading: boolean = true;

  private tags: TagInterface[] = [];

  private userRoles: object = Roles;

  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private async created(): Promise<void> {
    if (!this.$route.params.id) {
      this.$errorDialog.show('Feedback id not provided');
      return;
    }

    try {
      await this.fetchScores();
      await this.fetchTags();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(filter?: FilterRequestData): Promise<void> {
    try {
      this.loading = true;
      const feedback = await this.feedbackRepositoryV2.getChatFeedback(Number(this.$route.params.id));
      await this.fetchFeedbackUsersData(feedback);
      this.feedback = feedback;
      this.chat = feedback.chat;
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:tag:read')) {
      return;
    }

    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:score:type:read')) {
      return;
    }

    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();

    this.scoreTypes.forEach((scoreType) => {
      this.scoreTypesMap[scoreType.id] = scoreType.label;
    });

    this.scoreGrades.forEach((scoreGrade) => {
      this.scoreGradesMap[scoreGrade.id] = scoreGrade.label;
    });
  }

  private async fetchFeedbackUsersData(feedback: ChatFeedbackInterface): Promise<void> {
    const reviews = feedback.chat.reviews.filter((r) => r.reviewedId === feedback.feedbackTo);
    if (!reviews || !reviews.length) {
      this.$errorDialog.show('Feedback not found for given user!');
      return;
    }

    const ids: string[] = [
      feedback.feedbackTo,
      feedback.feedbackFrom,
      ...reviews.map((review) => review.reviewedId),
      ...reviews.map((review) => review.reviewerId),
    ];

    ids.push(...reviews.map((review) => review.comments).flat().map((comment) => comment.author));

    feedback.chat.messages.forEach((message) => {
      if (!message.comments.length) {
        return;
      }

      message.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    feedback.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    if (feedback.chat.notes && feedback.chat.notes.length) {
      feedback.chat.notes.forEach((note) => {
        if (!ids.includes(note.author)) {
          ids.push(note.author);
        }
      });
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });

    const [firstName, lastName] = this.$store.getters.name.split(' ');
    this.profilesMap[this.$store.getters.id] = {
      userId: this.$store.getters.id,
      profileId: '',
      firstName,
      lastName,
      fullName: '',
    };
  }
}
